import _ from 'lodash';
import XXH from 'xxhashjs';
import { z } from 'zod';
import type { Topic } from './topic';
import { generatorSystemZod } from './types';

export enum ConfigActions {
  HASH = 'hash',
}

export interface MaskingConfig {
  action: ConfigActions;
  path: string;
}

export class EventMasker {
  private topicMaskingConfig: Map<string, Set<MaskingConfig>>;
  private seed: number;

  private eventEntryZodMasked = z.object({
    uuid: z.string(),
    topic_id: z.string(),
    payload: z.any(),
    published_at: z.date(),
    correlation_id: z.string(),
    generator_system: generatorSystemZod,
    originator_uri: z.string().transform(this.masking),
    _key_builder_catalog: z
      .string()
      .optional()
      .nullable()
      .transform(this.masking),
    _key_marketplace: z.string().optional().nullable().transform(this.masking),
    _key_cgp: z.string().optional().nullable(),
    _key_coordinator: z.string().optional().nullable().transform(this.masking),
  });

  constructor(
    seed: number,
    defaultMaskingConfig: Map<string, Set<MaskingConfig>> = new Map(),
  ) {
    this.seed = seed;
    this.topicMaskingConfig = defaultMaskingConfig;
  }

  private masking(value: string | null | undefined): string | null | undefined {
    return value ? XXH.h64(value, this.seed).toString(16) : value;
  }

  private searchPath(object: any, path: string) {
    if (object)
      if (_.isArray(object))
        object.forEach(item => this.searchPath(item, path));
      else {
        const hasPath = _.has(object, path);

        if (hasPath) {
          const subObject = _.get(object, path);
          const stringified = _.isObject(subObject)
            ? JSON.stringify(subObject)
            : _.toString(subObject);
          _.set(object, path, this.masking(stringified));
        } else {
          const [firstLevel, ...otherLevels] = path.split('.');

          if (firstLevel !== path) {
            const hasFirstLevel = _.has(object, firstLevel);

            if (hasFirstLevel) {
              const subObject = _.get(object, firstLevel);
              this.searchPath(subObject, otherLevels.join('.'));
            }
          }
        }
      }
  }

  private maskPayload(topic_id: string, payload: any): any {
    const configs = this.topicMaskingConfig.get(topic_id);

    configs?.forEach((config: MaskingConfig) =>
      this.searchPath(payload, config.path),
    );

    return payload;
  }

  public mask(event: any): any {
    const maskedEvent = this.eventEntryZodMasked.parse(event);

    const maskedPayload = this.maskPayload(
      maskedEvent.topic_id,
      maskedEvent.payload,
    );

    return { ...maskedEvent, payload: maskedPayload };
  }

  public getTopicMaskingConfig(): Map<string, Set<MaskingConfig>> {
    return this.topicMaskingConfig;
  }

  public getTopicsWithoutDefaultConfig(topics: string[]): string[] {
    return topics.filter(topic => !this.topicMaskingConfig.has(topic));
  }

  public setTopicMaskingConfig(
    topicMaskingConfig: Map<string, Set<MaskingConfig>>,
  ): void {
    this.topicMaskingConfig = topicMaskingConfig;
  }
}

/**
 * Derives the default masking configuration Map (as consumed by
 * {@link EventMasker}) from a list of topics, using each topic's own
 * `masking` field (populated via the `masking` selector passed to
 * `createTopic`).
 */
export function buildMaskingConfigFromTopics(
  topics: Topic[],
): Map<string, Set<MaskingConfig>> {
  return new Map(topics.map(t => [t.topic.id, new Set(t.masking)]));
}
